import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyAPI, bookingAPI, adminAPI, squareAPI } from '../services/api';
import Layout from '../components/Layout';
import BookingCalendar from '../components/BookingCalendar';
import Icon from '../components/Icon';
import BikePassportModal from '../components/BikePassportModal';
import { ONBOARDING_CALL_URL } from '../utils/externalLinks';
import '../styles/Dashboard.css';
import '../styles/Admin.css';

/* eslint-disable unicode-bom */


// Day Preview Component - Shows bookings for a specific day in timeline format
function DayPreview({ date, bookings, highlightBookingId, darkMode = true }) {
  const dayBookings = bookings.filter(b => b.booking_date === date)
    .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  
  const timeSlots = [];
  for (let hour = 8; hour <= 18; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
  }
  
  const getStatusColor = (status, isHighlighted) => {
    if (isHighlighted) return { bg: 'rgba(26, 40, 17, 0.4)', border: '#1A2811', text: '#ffffff' };
    switch (status) {
      case 'pending': return { bg: 'rgba(245, 158, 11, 0.2)', border: '#f59e0b', text: '#fbbf24' };
      case 'confirmed': return { bg: 'rgba(102, 126, 234, 0.2)', border: '#667eea', text: '#a5b4fc' };
      case 'in_progress': return { bg: 'rgba(78, 205, 196, 0.2)', border: '#4ecdc4', text: '#4ecdc4' };
      case 'completed': return { bg: 'rgba(26, 40, 17, 0.2)', border: '#1A2811', text: '#4A8C18' };
      default: return { bg: 'rgba(255, 255, 255, 0.1)', border: '#6b7280', text: '#9ca3af' };
    }
  };
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '280px', overflowY: 'auto' }}>
      {timeSlots.map(slot => {
        const slotHour = parseInt(slot.substring(0, 2));
        const slotBookings = dayBookings.filter(b => {
          const bookingHour = parseInt(b.start_time?.substring(0, 2) || '0');
          return bookingHour === slotHour;
        });
        
        return (
          <div key={slot} style={{ 
            display: 'flex', 
            minHeight: '32px',
            borderBottom: '1px solid rgba(255,255,255,0.05)'
          }}>
            <div style={{ 
              width: '50px', 
              fontSize: '11px', 
              color: 'rgba(255,255,255,0.4)', 
              paddingRight: '8px',
              paddingTop: '4px',
              textAlign: 'right',
              flexShrink: 0
            }}>
              {slot}
            </div>
            <div style={{ flex: 1, paddingLeft: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {slotBookings.map(booking => {
                const isHighlighted = booking.id === highlightBookingId;
                const colors = getStatusColor(booking.status, isHighlighted);
                return (
                  <div 
                    key={booking.id}
                    style={{
                      padding: '4px 8px',
                      background: colors.bg,
                      borderLeft: `3px solid ${colors.border}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      color: colors.text,
                      transform: isHighlighted ? 'scale(1.02)' : 'scale(1)',
                      boxShadow: isHighlighted ? '0 2px 8px rgba(16, 185, 129, 0.3)' : 'none',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ fontWeight: '600', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{booking.start_time?.substring(0, 5)} - {booking.end_time?.substring(0, 5)}</span>
                      {isHighlighted && <span style={{ fontSize: '10px' }}>← NEW</span>}
                    </div>
                    <div style={{ opacity: 0.8, fontSize: '10px' }}>
                      {booking.service_template?.name || 'Service'} • {booking.bike?.brand}
                    </div>
                    {booking.technician && (
                      <div style={{ opacity: 0.6, fontSize: '9px' }}>
                        <Icon name="technician" color="muted" darkMode={darkMode} size={9} style={{ verticalAlign: 'middle', marginRight: '2px' }} />
                        {booking.technician.name}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {dayBookings.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          color: 'rgba(255,255,255,0.4)',
          fontSize: '13px'
        }}>
          No other bookings on this day
        </div>
      )}
    </div>
  );
}

// Check if user is within their first week
const isNewUser = (user) => {
  if (!user?.created_at) return false;
  const createdAt = new Date(user.created_at);
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return createdAt > oneWeekAgo;
};

function AdminDashboard({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const navigate = useNavigate();
  const [companyInfo, setCompanyInfo] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [showOnboardingPrompt, setShowOnboardingPrompt] = useState(() => {
    // Check if user dismissed the onboarding prompt
    const dismissed = localStorage.getItem('dismissedOnboardingAdmin');
    return !dismissed && isNewUser(user);
  });
  const [stats, setStats] = useState({
    bookingsToday: 0,
    pendingBookings: 0,
    totalBookings: 0,
    technicians: 0,
    serviceTemplates: 0,
    totalBikes: 0,
    connectedUsers: 0,
    unansweredReviews: 0
  });
  const [loading, setLoading] = useState(true);
  const [showQuickAddBike, setShowQuickAddBike] = useState(false);
  const [showQuickAddService, setShowQuickAddService] = useState(false);
  const [showBookingDetailModal, setShowBookingDetailModal] = useState(false);
  const [selectedBookingDetail, setSelectedBookingDetail] = useState(null);
  const [passportBike, setPassportBike] = useState(null);
  const [bikes, setBikes] = useState([]);
  const [lastAddedBikeId, setLastAddedBikeId] = useState('');
  const [quickBikeForm, setQuickBikeForm] = useState({ vin: '', brand: '', model: '', year: new Date().getFullYear() });
  const [quickServiceForm, setQuickServiceForm] = useState({ bike_id: '', service_date: new Date().toISOString().split('T')[0], service_time: '', description: '', total_cost: 0 });
  const [quickFormError, setQuickFormError] = useState('');
  const [quickFormSuccess, setQuickFormSuccess] = useState('');
  const [allBookings, setAllBookings] = useState([]);
  const [newBookings, setNewBookings] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [selectedPreviewBooking, setSelectedPreviewBooking] = useState(null);
  const [showAssignmentHelp, setShowAssignmentHelp] = useState(false);
  const [bookingInterests, setBookingInterests] = useState({ total_count: 0, recent_count: 0 });
  const [squareAvailable, setSquareAvailable] = useState(false);
  const [squareDeviceMode, setSquareDeviceMode] = useState(null);
  const [assignmentLogicInfo, setAssignmentLogicInfo] = useState({
    title: 'Smart Technician Assignment',
    description: 'When customers book appointments, the system automatically assigns technicians using intelligent load balancing.',
    logic: [
      {
        priority: 1,
        rule: 'Time-Off Check',
        description: 'Never assigns bookings to technicians on vacation or sick leave.'
      },
      {
        priority: 2,
        rule: 'Adjacent Slot Preference',
        description: 'If a technician has a booking right before or after, they get priority.'
      },
      {
        priority: 3,
        rule: 'Load Balancing',
        description: 'Among available technicians, the one with the least work hours gets the new booking.'
      }
    ],
    tips: [
      'Customers only see available slots - they cannot choose a technician',
      'Technicians can set their own time-off from their dashboard',
      'Bookings can be manually reassigned after creation'
    ]
  });
  const isSuperAdmin = user.role === 'super_admin';
  const notificationRef = useRef(null);

  const prefillQuickServiceBike = () => {
    const defaultBikeId = lastAddedBikeId || bikes?.[0]?.id || '';
    if (defaultBikeId) {
      setQuickServiceForm(prev => ({ ...prev, bike_id: defaultBikeId }));
    }
  };

  const openQuickAddService = () => {
    prefillQuickServiceBike();
    setShowQuickAddService(true);
  };

  useEffect(() => {
    fetchData();
    fetchBikes();
    fetchAssignmentLogicInfo();
  }, [isSuperAdmin]);
  
  const fetchAssignmentLogicInfo = async () => {
    try {
      const response = await bookingAPI.getAssignmentLogicInfo();
      setAssignmentLogicInfo(response.data);
    } catch (err) {
      console.log('Could not fetch assignment logic info, using defaults');
      // Fallback data if API fails
      setAssignmentLogicInfo({
        title: 'Smart Technician Assignment',
        description: 'When customers book appointments, the system automatically assigns technicians using intelligent load balancing.',
        logic: [
          {
            priority: 1,
            rule: 'Time-Off Check',
            description: 'Never assigns bookings to technicians on vacation or sick leave. Set up time-off in: Settings > Technicians.'
          },
          {
            priority: 2,
            rule: 'Adjacent Slot Preference',
            description: 'If a technician has a booking right before or after, they get priority. This minimizes gaps in the calendar.'
          },
          {
            priority: 3,
            rule: 'Load Balancing',
            description: 'Among available technicians, the one with the least work hours for that day gets the new booking. This ensures even distribution.'
          }
        ],
        tips: [
          'Customers only see available slots - they cannot choose a technician',
          'Technicians can set their own time-off from their dashboard',
          'Bookings can be manually reassigned after creation',
          'The system checks availability in real-time to prevent double bookings'
        ]
      });
    }
  };

  // Keyboard shortcuts for quick actions (Alt on Windows, Cmd on Mac - Option+letter on Mac produces special chars)
  useEffect(() => {
    const isMod = (e) => e.altKey || e.metaKey;
    const handleKeyDown = (e) => {
      // Only trigger if not in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }
      
      // Alt/Cmd + B = Quick add bike (Cmd on Mac, Alt on Windows)
      if (isMod(e) && (e.key === 'b' || e.key.toLowerCase() === 'b')) {
        e.preventDefault();
        setShowQuickAddBike(true);
      }
      // Alt/Cmd + S = Add service
      if (isMod(e) && (e.key === 's' || e.key.toLowerCase() === 's')) {
        e.preventDefault();
        navigate('/admin/management?tab=services&action=add');
      }
      // Alt/Cmd + C = Navigate to calendar
      if (isMod(e) && (e.key === 'c' || e.key.toLowerCase() === 'c')) {
        e.preventDefault();
        navigate('/calendar');
      }
      // Alt/Cmd + A = Navigate to analytics
      if (isMod(e) && (e.key === 'a' || e.key.toLowerCase() === 'a')) {
        e.preventDefault();
        navigate('/admin/analytics');
      }
      // Escape = Close modals
      if (e.key === 'Escape') {
        setShowQuickAddBike(false);
        setShowQuickAddService(false);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  const fetchBikes = async () => {
    try {
      const response = await adminAPI.getAllBikes();
      const bikeList = response.data.bikes || [];
      setBikes(bikeList);
      const latestBike = bikeList
        .slice()
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
      setLastAddedBikeId(latestBike?.id || '');
    } catch (e) {
      console.log('Could not load bikes');
      setLastAddedBikeId('');
    }
  };

  useEffect(() => {
    if (lastAddedBikeId && !quickServiceForm.bike_id) {
      setQuickServiceForm(prev => ({ ...prev, bike_id: lastAddedBikeId }));
    }
  }, [lastAddedBikeId, quickServiceForm.bike_id]);

  const handleQuickAddBike = async (e) => {
    e.preventDefault();
    setQuickFormError('');
    try {
      const response = await adminAPI.addBike(quickBikeForm);
      const newBike = response?.data?.bike;
      const newBikeId = newBike?.id;
      if (newBikeId) {
        setLastAddedBikeId(newBikeId);
        setQuickServiceForm(prev => ({ ...prev, bike_id: newBikeId }));
      }
      // Check if bike was marked as stolen (matches a reported stolen bike)
      if (newBike?.is_stolen) {
        setQuickFormSuccess('WARNING: This bike matches a STOLEN bike report! Frame number was previously reported stolen. The bike has been created but flagged.');
        setTimeout(() => setQuickFormSuccess(''), 10000); // Show longer for stolen warning
      } else {
        setQuickFormSuccess('Bike added successfully!');
        setTimeout(() => setQuickFormSuccess(''), 3000);
      }
      setShowQuickAddBike(false);
      setQuickBikeForm({ vin: '', brand: '', model: '', year: new Date().getFullYear() });
      fetchBikes();
    } catch (err) {
      setQuickFormError(err.response?.data?.error || 'Failed to add bike');
    }
  };

  const handleQuickAddService = async (e) => {
    e.preventDefault();
    setQuickFormError('');
    try {
      const formData = new FormData();
      formData.append('bike_id', quickServiceForm.bike_id);
      formData.append('service_date', quickServiceForm.service_date);
      if (quickServiceForm.service_time) {
        formData.append('service_time', quickServiceForm.service_time);
      }
      formData.append('description', quickServiceForm.description);
      formData.append('total_cost', quickServiceForm.total_cost);
      await adminAPI.addService(formData);
      setQuickFormSuccess('Service added successfully!');
      setShowQuickAddService(false);
      setQuickServiceForm({ bike_id: lastAddedBikeId || '', service_date: new Date().toISOString().split('T')[0], service_time: '', description: '', total_cost: 0 });
      fetchData();
      setTimeout(() => setQuickFormSuccess(''), 3000);
    } catch (err) {
      setQuickFormError(err.response?.data?.error || 'Failed to add service');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch company info if not super admin
      if (!isSuperAdmin) {
        try {
          const companyRes = await companyAPI.getCompanyInfo();
          setCompanyInfo(companyRes.data.company);

          try {
            const squareStatusRes = await squareAPI.getStatus();
            const isSquareReady =
              Boolean(squareStatusRes?.data?.feature_available) &&
              squareStatusRes?.data?.connection?.status === 'connected';
            setSquareAvailable(isSquareReady);
            setSquareDeviceMode(squareStatusRes?.data?.device_mode || squareStatusRes?.data?.connection?.square_device_mode || null);
            if (isSquareReady && !squareStatusRes?.data?.missing_appointments_scope) {
              squareAPI.syncBookings().catch(() => {});
            }
          } catch (squareErr) {
            setSquareAvailable(false);
            setSquareDeviceMode(null);
          }

          // For Basic and Professional tier companies, fetch booking interests
          const tier = companyRes.data.company?.subscription_tier;
          if (tier === 'basic' || tier === 'professional' || !tier) {
            try {
              const interestsRes = await bookingAPI.getBookingInterests();
              setBookingInterests({
                total_count: interestsRes.data.total_count || 0,
                recent_count: interestsRes.data.recent_count || 0
              });
            } catch (e) {
              console.log('Could not load booking interests');
            }
          }
        } catch (e) {
          console.log('Could not load company info');
          setSquareAvailable(false);
        }
      }

      // Fetch stats
      let allBookings = [];
      let technicians = [];
      let templates = [];
      
      try {
        // Fetch bookings from last 7 days to show in calendar and find new ones
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const bookingsRes = await bookingAPI.getAllBookings({ 
          date_from: weekAgo.toISOString().split('T')[0] 
        });
        const fetchedBookings = bookingsRes.data.bookings || [];
        setAllBookings(fetchedBookings);
        
        // Get recent bookings (last 5), sorted by creation date (newest first)
        const sortedBookings = fetchedBookings.sort((a, b) => {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB - dateA;
        });
        setRecentBookings(sortedBookings.slice(0, 5));
        
        // Find new bookings (only pending - confirmed ones should disappear)
        const newBookingsList = fetchedBookings.filter(b => b.status === 'pending');
        setNewBookings(newBookingsList);
      } catch (e) {
        console.log('Could not load bookings');
      }
      
      try {
        const techniciansRes = await bookingAPI.getTechnicians(true);
        technicians = techniciansRes.data.technicians || [];
      } catch (e) {
        console.log('Could not load technicians');
      }
      
      try {
        const templatesRes = await bookingAPI.getServiceTemplates(true);
        templates = templatesRes.data.templates || [];
      } catch (e) {
        console.log('Could not load service templates');
      }

      const todayBookings = allBookings.filter(b => b.booking_date === today);
      const pendingBookings = allBookings.filter(b => b.status === 'pending');

      let services = [];
      try {
        const servicesRes = await adminAPI.getAllServices();
        services = servicesRes.data.services || [];
      } catch (e) {
        console.log('Could not load services');
      }

      // Fetch unanswered reviews count
      let unansweredReviews = 0;
      try {
        const { reviewAPI } = await import('../services/api');
        const reviewsRes = await reviewAPI.getCompanyReviews({ status: 'unanswered' });
        unansweredReviews = reviewsRes.data.stats?.unanswered || 0;
      } catch (e) {
        console.log('Could not load reviews');
      }
      
      setStats({
        bookingsToday: todayBookings.length,
        pendingBookings: pendingBookings.length,
        totalBookings: allBookings.length,
        totalServices: services.length,
        technicians: technicians.length,
        serviceTemplates: templates.length,
        unansweredReviews: unansweredReviews,
        totalBikes: 0, // Will be populated if needed
        connectedUsers: 0 // Will be populated if needed
      });
    } catch (err) {
      console.log('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#4A8C18';
      case 'pending': return '#f59e0b';
      case 'completed': return '#4A8C18';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleStatusUpdate = async (bookingId, newStatus, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    setUpdatingStatus(prev => ({ ...prev, [bookingId]: true }));
    try {
      await bookingAPI.updateBookingStatus(bookingId, newStatus);
      
      // Optimistic update: immediately remove from newBookings if confirmed
      if (newStatus === 'confirmed') {
        setNewBookings(prev => prev.filter(b => b.id !== bookingId));
        // Also clear the preview if this booking was selected
        if (selectedPreviewBooking?.id === bookingId) {
          setSelectedPreviewBooking(null);
        }
      }
      
      // Refresh bookings in background
      fetchData();
    } catch (err) {
      console.error('Failed to update booking status:', err);
      // On error, refresh to get correct state
      fetchData();
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const isLightMode = !darkMode;
  const quickModalStyle = {
    background: isLightMode ? '#ffffff' : '#1a1a3e',
    border: isLightMode ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)',
    borderRadius: '20px',
    padding: '28px',
    maxWidth: '450px',
    width: '100%'
  };
  const quickLabelStyle = {
    display: 'block',
    marginBottom: '8px',
    color: isLightMode ? '#374151' : 'rgba(255,255,255,0.7)',
    fontSize: '14px'
  };
  const quickInputStyle = {
    width: '100%',
    padding: '12px',
    borderRadius: '10px',
    border: isLightMode ? '2px solid #d1d5db' : '1px solid rgba(255,255,255,0.1)',
    background: isLightMode ? '#ffffff' : 'var(--bg-overlay-medium)',
    color: isLightMode ? '#1f2937' : '#ffffff',
    fontSize: '14px',
    boxShadow: isLightMode ? 'inset 0 1px 2px rgba(0,0,0,0.05)' : 'none',
    outline: 'none'
  };
  const quickTextareaStyle = {
    ...quickInputStyle,
    resize: 'vertical'
  };
  const quickHelperTextStyle = {
    fontSize: '12px',
    color: isLightMode ? '#4b5563' : 'rgba(255,255,255,0.5)',
    marginTop: '4px'
  };

  const handleBookingClick = (bookingId, bookingDate, e) => {
    if (e) {
      e.stopPropagation();
    }
    // Navigate to calendar with booking ID and date for direct navigation
    const params = new URLSearchParams();
    params.append('bookingId', bookingId);
    if (bookingDate) {
      params.append('date', bookingDate);
    }
    navigate(`/calendar?${params.toString()}`);
  };

  return (
    <Layout
      user={user}
      onLogout={onLogout}
      pageTitle={isSuperAdmin ? 'Admin Command Center' : 'Company Dashboard'}
      pageSubtitle={companyInfo ? `Managing ${companyInfo.name}` : 'Manage your business operations'}
      darkMode={darkMode}
      onToggleDarkMode={onToggleDarkMode}
      subscriptionTier={companyInfo?.subscription_tier}
    >
      <div className="dashboard-shell">
        <div className="dashboard-shell-header">
          <div>
            <p className="dashboard-shell-eyebrow">Frame-Book • The Global Standard in Bicycle Service</p>
            <div className="dashboard-shell-title">
              {isSuperAdmin ? 'Command Center' : 'Service Operations'}
            </div>
            <p className="dashboard-shell-subtitle">
              {companyInfo ? `Managing ${companyInfo.name}` : 'Overview of bookings, technicians and templates.'}
            </p>
          </div>
          <div className="dashboard-shell-meta">
            <span className="dashboard-shell-badge">
              Today · {new Date().toLocaleDateString('en-GB')}
            </span>
          </div>
        </div>

        {/* Onboarding Call Prompt - for new company admins within first week */}
        {showOnboardingPrompt && (
          <div style={{
            marginBottom: '24px',
            padding: '20px',
            background: 'linear-gradient(135deg, rgba(61, 90, 32, 0.15) 0%, rgba(74, 140, 24, 0.15) 100%)',
            borderRadius: '16px',
            border: '1px solid rgba(61, 90, 32, 0.3)',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '10px',
                background: 'rgba(74, 140, 24, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Icon name="calendar" color="brand" darkMode={darkMode} size={22} />
              </div>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <h3 style={{ 
                  margin: '0 0 6px 0', 
                  color: darkMode ? '#4A8C18' : '#3d5a20',
                  fontSize: '16px',
                  fontWeight: '700'
                }}>
                  Got questions? Book an onboarding call!
                </h3>
                <p style={{ 
                  margin: 0, 
                  color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                  fontSize: '13px'
                }}>
                  Get a free 30-minute walkthrough with our team to set up your company profile and learn all the features.
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <a 
                  href={ONBOARDING_CALL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 18px',
                    background: 'var(--lime-primary)',
                    color: '#ffffff',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '13px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Icon name="calendar" customColor="#ffffff" size={14} />
                  Book call
                </a>
                <button
                  onClick={() => {
                    localStorage.setItem('dismissedOnboardingAdmin', 'true');
                    setShowOnboardingPrompt(false);
                  }}
                  style={{
                    background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                    border: 'none',
                    color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '8px 10px',
                    borderRadius: '8px',
                    lineHeight: 1
                  }}
                  title="Dismiss"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Enterprise: No technicians - online booking won't work */}
        {(companyInfo?.subscription_tier === 'enterprise' || companyInfo?.subscription_tier === 'lifetime') && stats.technicians < 1 && !loading && (
          <div style={{
            marginBottom: '24px',
            padding: '16px 20px',
            background: 'rgba(245, 158, 11, 0.12)',
            borderRadius: '12px',
            border: '1px solid rgba(245, 158, 11, 0.35)',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            flexWrap: 'wrap'
          }}>
            <Icon name="warning" color="warning" darkMode={darkMode} size={24} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: '200px' }}>
              <p style={{ margin: 0, fontWeight: 600, color: darkMode ? '#fbbf24' : '#b45309', fontSize: '14px' }}>
                Add at least one technician to enable online booking
              </p>
              <p style={{ margin: '4px 0 0 0', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)', fontSize: '13px' }}>
                Customers cannot book appointments until technicians are set up. Go to Settings → Technicians to add your first technician.
              </p>
            </div>
            <button
              onClick={() => navigate('/admin/company-settings?tab=technicians')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 18px',
                background: '#f59e0b',
                color: '#1a1a1a',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '13px',
                flexShrink: 0
              }}
            >
              <Icon name="technician" customColor="#1a1a1a" size={16} />
              Add Technician
            </button>
          </div>
        )}

        {/* Compact Stats Row - Only for Enterprise tier (has booking) */}
        {(companyInfo?.subscription_tier === 'enterprise' || companyInfo?.subscription_tier === 'lifetime') && (
        <div className="admin-stats-row">
          <div
            onClick={() => navigate('/calendar')}
            className="admin-stat-card"
            style={{
              background: 'rgba(61, 90, 32, 0.12)',
              border: '1px solid rgba(61, 90, 32, 0.25)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(61, 90, 32, 0.2)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(61, 90, 32, 0.12)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Icon name="calendar" color="brand" darkMode={darkMode} size={20} />
            <div>
              <div className="stat-value" style={{ fontSize: '20px', fontWeight: '700', color: '#3d5a20' }}>
                {loading ? '...' : stats.bookingsToday}
              </div>
              <div className="stat-label" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }}>
                Today
              </div>
            </div>
          </div>

          <div
            onClick={() => navigate('/calendar')}
            className="admin-stat-card"
            style={{
              background: 'rgba(245, 158, 11, 0.12)',
              border: '1px solid rgba(245, 158, 11, 0.25)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(245, 158, 11, 0.2)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(245, 158, 11, 0.12)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Icon name="loading" color="warning" darkMode={darkMode} size={20} />
            <div>
              <div className="stat-value" style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b' }}>
                {loading ? '...' : stats.pendingBookings}
              </div>
              <div className="stat-label" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }}>
                Pending
              </div>
            </div>
          </div>

          <div
            onClick={() => navigate('/calendar')}
            className="admin-stat-card"
            style={{
              background: 'rgba(59, 130, 246, 0.12)',
              border: '1px solid rgba(59, 130, 246, 0.25)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(59, 130, 246, 0.12)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Icon name="upload" color="info" darkMode={darkMode} size={20} />
            <div>
              <div className="stat-value" style={{ fontSize: '20px', fontWeight: '700', color: '#3b82f6' }}>
                {loading ? '...' : newBookings.length}
              </div>
              <div className="stat-label" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }}>
                New incoming
              </div>
            </div>
          </div>

        </div>
        )}

        {quickFormSuccess && (
          <div className="success" style={{ marginBottom: '20px' }}>{quickFormSuccess}</div>
        )}

        {/* Booking interests notice - subtle, Basic/Professional tier only */}
        {(companyInfo?.subscription_tier === 'basic' || companyInfo?.subscription_tier === 'professional') && bookingInterests.total_count > 0 && (
          <div style={{
            background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
            borderRadius: '12px',
            padding: '14px 18px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px'
          }}>
            <Icon name="calendar" color="secondary" darkMode={darkMode} size={20} style={{ flexShrink: 0, opacity: 0.8 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ 
                color: darkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)', 
                fontSize: '14px'
              }}>
                <strong>{bookingInterests.total_count}</strong> {bookingInterests.total_count === 1 ? 'customer has' : 'customers have'} wanted to book online so far. Consider upgrading to Enterprise to enable online booking.
              </span>
            </div>
            <button
              onClick={() => navigate('/billing')}
              className="btn btn-secondary"
              style={{ flexShrink: 0, padding: '8px 16px', fontSize: '13px', whiteSpace: 'nowrap' }}
            >
              Learn more
            </button>
          </div>
        )}

        {/* Quick Add Actions */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(61, 90, 32, 0.12) 0%, rgba(61, 90, 32, 0.04) 100%)',
          border: '1px solid rgba(61, 90, 32, 0.25)',
          borderRadius: '24px',
          padding: '24px',
          marginBottom: '28px',
          boxShadow: 'var(--shadow-2xl)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{
              fontSize: '16px',
              color: '#3d5a20',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              margin: 0
            }}>
              <Icon name="play" color="brand" darkMode={darkMode} size={16} />
              Quick Add
            </h3>
          </div>
          <div className="admin-quick-add-grid">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
              <button
                onClick={() => setShowQuickAddBike(true)} 
                className="btn btn-primary"
                style={{justifyContent: 'center', gap: '12px', padding: '16px 24px', width: '100%'}}
              >
                <Icon name="bike" customColor={darkMode ? '#ffffff' : '#111827'} size={24} />
                <span style={{color: darkMode ? '#ffffff' : '#111827'}}>+ Add Bike</span>
              </button>
              <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', padding: '2px 6px', background: 'var(--bg-overlay-medium)', borderRadius: '4px' }}>Alt+B / ⌘B</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
              <button
                onClick={() => navigate('/admin/management?tab=services&action=add')} 
                className="btn btn-primary"
                style={{justifyContent: 'center', gap: '12px', padding: '16px 24px', width: '100%'}}
              >
                <Icon name="service" customColor={darkMode ? '#ffffff' : '#111827'} size={24} />
                <span style={{color: darkMode ? '#ffffff' : '#111827'}}>+ Add Service</span>
              </button>
              <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', padding: '2px 6px', background: 'var(--bg-overlay-medium)', borderRadius: '4px' }}>Alt+S / ⌘S</span>
            </div>
            {squareAvailable && squareDeviceMode === 'terminal' && (
              <button
                onClick={() => navigate('/admin/management?tab=services&action=invoice')}
                className="btn btn-secondary"
              style={{
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '16px 24px',
                  color: darkMode ? '#ffffff' : '#111827',
                  borderColor: darkMode ? undefined : 'rgba(255,255,255,0.35)'
                }}
              >
                <Icon name="payment" customColor={darkMode ? '#ffffff' : '#111827'} size={24} />
                <span style={{ color: darkMode ? '#ffffff' : '#111827' }}>Create Service Ticket</span>
              </button>
            )}
            {/* View Calendar - Only for Enterprise tier */}
            {companyInfo?.subscription_tier === 'enterprise' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
              <button
                onClick={() => navigate('/calendar')} 
                className="btn btn-secondary"
                style={{
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '16px 24px',
                  width: '100%',
                  color: darkMode ? '#ffffff' : '#111827',
                  borderColor: darkMode ? undefined : 'rgba(255,255,255,0.35)'
                }}
              >
                <Icon name="calendarMonth" customColor={darkMode ? '#ffffff' : '#111827'} size={24} />
                <span style={{ color: darkMode ? '#ffffff' : '#111827' }}>View Calendar</span>
              </button>
              <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', padding: '2px 6px', background: 'var(--bg-overlay-medium)', borderRadius: '4px' }}>Alt+C / ⌘C</span>
            </div>
            )}
            <button
              onClick={() => navigate('/admin/company-settings?tab=reviews')} 
              className="btn btn-secondary"
              style={{justifyContent: 'center', gap: '12px', padding: '16px 24px', position: 'relative'}}
            >
              <Icon name="star" color="warning" darkMode={darkMode} size={24} />
              <span>Reviews</span>
              {stats.unansweredReviews > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: darkMode ? '#4A8C18' : '#1A2811',
                  color: '#ffffff',
                  borderRadius: '10px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  fontWeight: '700',
                  minWidth: '18px',
                  textAlign: 'center'
                }}>
                  {stats.unansweredReviews}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* New Bookings Panel - Only for Enterprise tier */}
        {newBookings.length > 0 && (
          <div 
            ref={notificationRef}
            style={{
              background: 'linear-gradient(135deg, rgba(61, 90, 32, 0.15) 0%, rgba(16, 185, 129, 0.1) 100%)',
              border: '1px solid rgba(61, 90, 32, 0.3)',
              borderRadius: '20px',
              padding: '24px',
              marginBottom: '28px',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Subtle indicator dot instead of flashing */}
            <div style={{
              position: 'absolute',
              top: '24px',
              right: '24px',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: '#4A8C18',
              boxShadow: '0 0 8px rgba(74, 140, 24, 0.6)'
            }} />
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon name="email" color="primary" darkMode={darkMode} size={28} />
                <div>
                  <h3 style={{
                    fontSize: '18px',
                    color: '#ffffff',
                    margin: 0,
                    fontWeight: '600'
                  }}>
                    New Bookings
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
                    {newBookings.length} booking(s) awaiting confirmation
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowAssignmentHelp(!showAssignmentHelp)}
                title="Technician assignment logic"
                style={{
                  padding: '8px 12px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '8px',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Icon name="info" color="secondary" darkMode={darkMode} size={18} />
                <span>Help</span>
              </button>
            </div>
            
            {/* Assignment Logic Help Tooltip */}
            {showAssignmentHelp && assignmentLogicInfo && (
              <div style={{
                background: 'rgba(0,0,0,0.4)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                <h4 style={{ color: '#4A8C18', margin: '0 0 12px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="integration" color="brand" darkMode={darkMode} size={16} />
                  {assignmentLogicInfo.title}
                </h4>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', margin: '0 0 12px 0' }}>
                  {assignmentLogicInfo.description}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                  {assignmentLogicInfo.logic?.map((rule, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '8px 12px',
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: '8px'
                    }}>
                      <span style={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        background: 'rgba(74, 140, 24, 0.3)',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: '700',
                        flexShrink: 0
                      }}>
                        {rule.priority}
                      </span>
                      <div>
                        <div style={{ color: '#ffffff', fontWeight: '600', fontSize: '13px' }}>{rule.rule}</div>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '2px' }}>{rule.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px' }}>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Icon name="info" color="secondary" darkMode={darkMode} size={14} />
                    TIPPEK
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '16px', color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                    {assignmentLogicInfo.tips?.map((tip, idx) => (
                      <li key={idx} style={{ marginBottom: '4px' }}>{tip}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Bookings List with Calendar Preview */}
            <div className={`admin-bookings-preview-grid ${selectedPreviewBooking ? '' : 'no-preview'}`}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {newBookings.slice(0, 5).map((booking) => {
                  const isSelected = selectedPreviewBooking?.id === booking.id;
                  return (
                    <div
                      key={booking.id}
                      onClick={() => setSelectedPreviewBooking(isSelected ? null : booking)}
                      className="admin-booking-card"
                      style={{
                        background: isSelected ? 'rgba(61, 90, 32, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                        border: isSelected ? '2px solid rgba(61, 90, 32, 0.6)' : '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: '12px',
                        padding: '16px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div className="admin-booking-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <Icon name="bike" color="primary" darkMode={darkMode} size={16} />
                            <span style={{ fontSize: '15px', fontWeight: '600', color: '#ffffff' }}>
                              {booking.bike?.brand} {booking.bike?.model}
                            </span>
                          </div>
                          <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '4px' }}>
                            <span style={{ marginRight: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Icon name="service" color="secondary" darkMode={darkMode} size={14} />
                              {booking.service_template?.name}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Icon name="calendar" color="muted" darkMode={darkMode} size={12} />
                              {new Date(booking.booking_date).toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Icon name="schedule" color="muted" darkMode={darkMode} size={12} />
                              {booking.start_time?.substring(0, 5)} - {booking.end_time?.substring(0, 5)}
                            </span>
                            {booking.technician && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Icon name="technician" color="muted" darkMode={darkMode} size={12} />
                                {booking.technician.name}
                              </span>
                            )}
                          </div>
                          {booking.user && (
                            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Icon name="profile" color="muted" darkMode={darkMode} size={12} />
                              {booking.user.firstname} {booking.user.surname || booking.user.email}
                            </div>
                          )}
                        </div>
                        <div className="admin-booking-card-actions" style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
                          <span style={{
                            padding: '4px 10px',
                            background: booking.status === 'pending' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(26, 40, 17, 0.2)',
                            color: booking.status === 'pending' ? '#fbbf24' : '#4A8C18',
                            borderRadius: '8px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            {booking.status === 'pending' ? (
                              <>
                                <Icon name="loading" color="warning" darkMode={darkMode} size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                Pending
                              </>
                            ) : (
                              <>
                                <Icon name="success" color="success" darkMode={darkMode} size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                Confirmed
                              </>
                            )}
                          </span>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {booking.status === 'pending' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusUpdate(booking.id, 'confirmed', e);
                                }}
                                disabled={updatingStatus[booking.id]}
                                style={{
                                  padding: '6px 12px',
                                  background: '#4A8C18',
                                  border: 'none',
                                  color: '#ffffff',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: updatingStatus[booking.id] ? 'not-allowed' : 'pointer',
                                  opacity: updatingStatus[booking.id] ? 0.6 : 1
                                }}
                              >
                                {updatingStatus[booking.id] ? '...' : (
                                  <>
                                    <Icon name="success" color="primary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                    Accept
                                  </>
                                )}
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBookingClick(booking.id, booking.booking_date, e);
                              }}
                              style={{
                                padding: '6px 12px',
                                background: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                color: '#ffffff',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '500',
                                cursor: 'pointer'
                              }}
                            >
                              <Icon name="calendar" color="brand" darkMode={darkMode} size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {newBookings.length > 5 && (
                  <button
                    onClick={() => navigate('/calendar')}
                    style={{
                      background: 'rgba(61, 90, 32, 0.15)',
                      border: '1px solid rgba(61, 90, 32, 0.3)',
                      color: '#3d5a20',
                      borderRadius: '10px',
                      padding: '12px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}
                  >
                    + {newBookings.length - 5} more bookings →
                  </button>
                )}
              </div>
              
              {/* Calendar Day Preview */}
              {selectedPreviewBooking && (
                <div 
                  className="admin-bookings-preview-panel"
                  style={{
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}
                >
                  <h4 style={{ color: '#ffffff', margin: '0 0 12px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon name="calendarMonth" color="brand" darkMode={true} size={18} />
                    Calendar Preview - {new Date(selectedPreviewBooking.booking_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </h4>
                  <DayPreview 
                    date={selectedPreviewBooking.booking_date} 
                    bookings={allBookings} 
                    highlightBookingId={selectedPreviewBooking.id}
                    darkMode={darkMode}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calendar Section - Only for Enterprise tier */}
        {companyInfo && companyInfo.subscription_tier === 'enterprise' && (
          <div style={{
            background: 'var(--bg-overlay-light)',
            border: '1px solid rgba(241, 255, 217, 0.12)',
            borderRadius: '24px',
            padding: '24px',
            marginBottom: '28px'
          }}>
            <h3 style={{
              fontSize: '18px',
              color: '#ffffff',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <Icon name="calendar" color="brand" darkMode={darkMode} size={20} />
              Calendar
            </h3>
            <BookingCalendar 
              companyId={companyInfo.id} 
              darkMode={darkMode}
              onBookingSelect={(booking) => {
                // Navigate to calendar page for unified booking detail view (same as Calendar page)
                const params = new URLSearchParams();
                params.set('bookingId', booking.id);
                if (booking.booking_date) params.set('date', booking.booking_date);
                navigate(`/calendar?${params.toString()}`);
              }}
            />
          </div>
        )}

        {/* Main Content Grid */}
        <div className="admin-main-grid">
        {/* Recent Bookings - Only for Enterprise tier */}
        {companyInfo?.subscription_tier === 'enterprise' && (
        <div style={{
          background: 'var(--bg-overlay-light)',
          border: '1px solid rgba(241, 255, 217, 0.12)',
          borderRadius: '24px',
          padding: '24px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '18px',
              color: '#ffffff',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <Icon name="details" color="brand" darkMode={darkMode} size={20} />
              Recent Bookings
            </h3>
            <button 
              onClick={() => {
                // Navigate to admin dashboard with bookings tab
                // For now, go to calendar which shows bookings
                navigate('/calendar');
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--lime-primary)',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              View All →
            </button>
          </div>

          {loading ? (
            <div className="loading">Loading bookings...</div>
          ) : recentBookings.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: 'rgba(255, 255, 255, 0.5)'
            }}>
              <div style={{marginBottom: '12px', opacity: 0.3}}>
                <Icon name="calendar" color="muted" darkMode={darkMode} size={48} />
              </div>
              <p style={{margin: 0, fontSize: '14px'}}>No bookings yet</p>
            </div>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
              {recentBookings.map((booking) => (
                <div 
                  key={booking.id}
                  style={{
                    background: 'var(--bg-overlay-light)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    borderRadius: '12px',
                    padding: '16px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate('/calendar')}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <div style={{flex: 1}}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#ffffff',
                        marginBottom: '4px'
                      }}>
                        {booking.service_name || 'Service Booking'}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: 'rgba(255, 255, 255, 0.5)'
                      }}>
                        {booking.customer_name || booking.user_email || 'Customer'}
                      </div>
                    </div>
                    <span style={{
                      padding: '4px 10px',
                      background: `${getStatusColor(booking.status)}20`,
                      color: getStatusColor(booking.status),
                      borderRadius: '8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {getStatusLabel(booking.status)}
                    </span>
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.4)',
                    display: 'flex',
                    gap: '16px'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon name="calendar" color="muted" darkMode={darkMode} size={14} />
                      {booking.booking_date}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon name="schedule" color="muted" darkMode={darkMode} size={14} />
                      {booking.booking_time}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Company Info / Quick Stats - For Enterprise tier only (has booking) */}
        {companyInfo?.subscription_tier === 'enterprise' && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(233, 255, 183, 0.1) 0%, rgba(61, 90, 32, 0.05) 100%)',
          border: '1px solid rgba(241, 255, 217, 0.25)',
          borderRadius: '24px',
          padding: '24px'
        }}>
          <h3 style={{
            fontSize: '18px',
            color: darkMode ? '#ffffff' : '#111827',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Icon name="companies" color="brand" darkMode={darkMode} size={20} />
            {companyInfo ? 'Company Overview' : 'System Overview'}
          </h3>

          {companyInfo && (
            <div style={{marginBottom: '24px'}}>
              <div style={{
                fontSize: '24px',
                fontWeight: '600',
                color: darkMode ? '#ffffff' : '#111827',
                marginBottom: '12px'
              }}>
                {companyInfo.name}
              </div>
              {companyInfo.email && (
                <div style={{
                  fontSize: '14px',
                  color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(17,24,39,0.72)',
                  marginBottom: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Icon name="email" color="secondary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  {companyInfo.email}
                </div>
              )}
              {companyInfo.phone && (
                <div style={{
                  fontSize: '14px',
                  color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(17,24,39,0.72)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Icon name="view" color="secondary" darkMode={darkMode} size={14} />
                  {companyInfo.phone}
                </div>
              )}
            </div>
          )}

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <div style={{
              background: 'var(--bg-overlay-medium)',
              borderRadius: '12px',
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(17,24,39,0.72)', fontSize: '14px'}}>
                Active Bookings
              </span>
              <span style={{
                fontSize: '24px',
                fontWeight: '700',
                color: darkMode ? '#ffffff' : '#111827'
              }}>
                {stats.pendingBookings + stats.bookingsToday}
              </span>
            </div>
            
            <div style={{
              background: 'var(--bg-overlay-medium)',
              borderRadius: '12px',
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(17,24,39,0.72)', fontSize: '14px'}}>
                Team Members
              </span>
              <span style={{
                fontSize: '24px',
                fontWeight: '700',
                color: darkMode ? '#ffffff' : '#111827'
              }}>
                {stats.technicians}
              </span>
            </div>

            <div style={{
              background: 'var(--bg-overlay-medium)',
              borderRadius: '12px',
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(17,24,39,0.72)', fontSize: '14px'}}>
                Service Types
              </span>
              <span style={{
                fontSize: '24px',
                fontWeight: '700',
                color: darkMode ? '#ffffff' : '#111827'
              }}>
                {stats.serviceTemplates}
              </span>
            </div>
          </div>

          <div style={{marginTop: '24px'}}>
            <button 
              onClick={() => navigate('/admin/company-settings')} 
              className="btn btn-secondary"
              style={{width: '100%', justifyContent: 'center'}}
            >
              Manage Settings
            </button>
          </div>
        </div>
        )}

        {/* Basic/Professional Tier Quick Stats - Bikes, Services, Users */}
        {(companyInfo?.subscription_tier === 'basic' || companyInfo?.subscription_tier === 'professional') && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(233, 255, 183, 0.1) 0%, rgba(61, 90, 32, 0.05) 100%)',
          border: '1px solid rgba(241, 255, 217, 0.25)',
          borderRadius: '24px',
          padding: '24px'
        }}>
          <h3 style={{
            fontSize: '18px',
            color: darkMode ? '#ffffff' : '#111827',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Icon name="dashboard" color="brand" darkMode={darkMode} size={20} />
            Quick Stats
          </h3>
          
          <div className="admin-quick-stats-grid">
            <div 
              onClick={() => navigate('/admin/management?tab=bikes')}
              className="admin-quick-stat-item"
            >
              <Icon name="bike" color="brand" darkMode={darkMode} size={32} style={{ marginBottom: '12px' }} />
              <div className="admin-quick-stat-value">{stats.totalBikes}</div>
              <div className="admin-quick-stat-label">Bikes</div>
            </div>
            
            <div 
              onClick={() => navigate('/admin/management?tab=services')}
              className="admin-quick-stat-item"
            >
              <Icon name="service" color="brand" darkMode={darkMode} size={32} style={{ marginBottom: '12px' }} />
              <div className="admin-quick-stat-value">{stats.totalServices ?? 0}</div>
              <div className="admin-quick-stat-label">Services</div>
            </div>
            
            <div 
              onClick={() => navigate('/admin/management?tab=users')}
              className="admin-quick-stat-item"
            >
              <Icon name="profile" color="brand" darkMode={darkMode} size={32} style={{ marginBottom: '12px' }} />
              <div className="admin-quick-stat-value">{stats.connectedUsers}</div>
              <div className="admin-quick-stat-label">Users</div>
            </div>
          </div>

          <button 
            onClick={() => navigate('/admin/company-settings')} 
            className="btn btn-secondary"
            style={{width: '100%', justifyContent: 'center'}}
          >
            <Icon name="settings" color="secondary" darkMode={darkMode} size={16} style={{ marginRight: '8px' }} />
            Manage Settings
          </button>
        </div>
        )}
        </div>

      </div>

      {/* Quick Add Bike Modal */}
      {showQuickAddBike && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '24px'
          }}
          onClick={() => setShowQuickAddBike(false)}
        >
          <div
            className="quick-modal"
            style={{
              ...quickModalStyle
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: isLightMode ? '#111827' : '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="bike" color="primary" darkMode={darkMode} size={20} />
                Quick Add Bike
              </h2>
              <button
                onClick={() => setShowQuickAddBike(false)}
                style={{ background: 'var(--bg-overlay-medium)', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px', color: isLightMode ? '#111827' : 'rgba(255,255,255,0.6)' }}
              >×</button>
            </div>
            {quickFormError && <div className="error" style={{ marginBottom: '16px' }}>{quickFormError}</div>}
            <form onSubmit={handleQuickAddBike}>
              <div style={{ marginBottom: '16px' }}>
                <label style={quickLabelStyle}>Frame Number *</label>
                <input type="text" value={quickBikeForm.vin} onChange={(e) => setQuickBikeForm({...quickBikeForm, vin: e.target.value})} required maxLength={50} placeholder="Enter frame number" style={quickInputStyle} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={quickLabelStyle}>Brand *</label>
                <input type="text" value={quickBikeForm.brand} onChange={(e) => setQuickBikeForm({...quickBikeForm, brand: e.target.value})} required placeholder="e.g., Trek, Specialized" style={quickInputStyle} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={quickLabelStyle}>Model *</label>
                <input type="text" value={quickBikeForm.model} onChange={(e) => setQuickBikeForm({...quickBikeForm, model: e.target.value})} required placeholder="e.g., Domane SL 5" style={quickInputStyle} />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={quickLabelStyle}>Year *</label>
                <input type="number" value={quickBikeForm.year} onChange={(e) => setQuickBikeForm({...quickBikeForm, year: parseInt(e.target.value)})} required min={1900} max={new Date().getFullYear() + 1} style={quickInputStyle} />
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Add Bike</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowQuickAddBike(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Add Service Modal */}
      {showQuickAddService && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '24px'
          }}
          onClick={() => setShowQuickAddService(false)}
        >
          <div
            className="quick-modal"
            style={{
              ...quickModalStyle
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: isLightMode ? '#111827' : '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="service" color="brand" darkMode={!isLightMode} size={20} />
                Quick Add Service
              </h2>
              <button
                onClick={() => setShowQuickAddService(false)}
                style={{ background: 'var(--bg-overlay-medium)', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px', color: isLightMode ? '#111827' : 'rgba(255,255,255,0.6)' }}
              >×</button>
            </div>
            {quickFormError && <div className="error" style={{ marginBottom: '16px' }}>{quickFormError}</div>}
            <form onSubmit={handleQuickAddService}>
              <div style={{ marginBottom: '16px' }}>
                <label style={quickLabelStyle}>Bike *</label>
                <select value={quickServiceForm.bike_id} onChange={(e) => setQuickServiceForm({...quickServiceForm, bike_id: e.target.value})} required style={{ ...quickInputStyle, background: isLightMode ? '#f9fafb' : quickInputStyle.background }}>
                  <option value="">Select a bike</option>
                  {bikes.map(bike => (
                    <option key={bike.id} value={bike.id} style={bike.is_stolen ? { color: '#ef4444', fontWeight: 'bold' } : {}}>
                      {bike.is_stolen ? '🚨 STOLEN - ' : ''}{bike.brand} {bike.model} ({bike.vin})
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={quickLabelStyle}>Service Date *</label>
                <input type="date" value={quickServiceForm.service_date} onChange={(e) => setQuickServiceForm({...quickServiceForm, service_date: e.target.value})} required style={quickInputStyle} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={quickLabelStyle}>Service Time (Optional)</label>
                <input type="time" value={quickServiceForm.service_time} onChange={(e) => setQuickServiceForm({...quickServiceForm, service_time: e.target.value})} style={quickInputStyle} />
                <p style={quickHelperTextStyle}>Past time = completed, Future time = scheduled</p>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={quickLabelStyle}>Description</label>
                <textarea value={quickServiceForm.description} onChange={(e) => setQuickServiceForm({...quickServiceForm, description: e.target.value})} rows={3} placeholder="Service description..." style={quickTextareaStyle} />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={quickLabelStyle}>Total Cost (GBP) *</label>
                <input type="number" step="1" min="0" value={quickServiceForm.total_cost} onChange={(e) => setQuickServiceForm({...quickServiceForm, total_cost: parseFloat(e.target.value) || 0})} required style={quickInputStyle} />
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Add Service</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowQuickAddService(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Booking Detail Modal */}
      {showBookingDetailModal && selectedBookingDetail && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '24px'
          }}
          onClick={() => {
            setShowBookingDetailModal(false);
            setSelectedBookingDetail(null);
          }}
        >
          <div
            style={{
              background: isLightMode ? '#ffffff' : '#1a1a3e',
              border: isLightMode ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: isLightMode ? '#1a1a1a' : 'white', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="details" color="brand" darkMode={darkMode} size={20} />
                Booking Details
              </h2>
              <button
                onClick={() => {
                  setShowBookingDetailModal(false);
                  setSelectedBookingDetail(null);
                }}
                style={{
                  background: isLightMode ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  color: isLightMode ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)'
                }}
              >
                ×
              </button>
            </div>

            {/* Status Badge */}
            <div style={{
              padding: '12px 16px',
              borderRadius: '10px',
              background: selectedBookingDetail.status === 'completed' ? 'rgba(26, 40, 17, 0.15)' :
                          selectedBookingDetail.status === 'cancelled' ? 'rgba(239, 68, 68, 0.15)' :
                          selectedBookingDetail.status === 'confirmed' ? 'rgba(59, 130, 246, 0.15)' :
                          selectedBookingDetail.status === 'in_progress' ? 'rgba(78, 205, 196, 0.15)' :
                          'rgba(245, 158, 11, 0.15)',
              border: `1px solid ${selectedBookingDetail.status === 'completed' ? 'rgba(26, 40, 17, 0.3)' :
                                   selectedBookingDetail.status === 'cancelled' ? 'rgba(239, 68, 68, 0.3)' :
                                   selectedBookingDetail.status === 'confirmed' ? 'rgba(59, 130, 246, 0.3)' :
                                   selectedBookingDetail.status === 'in_progress' ? 'rgba(78, 205, 196, 0.3)' :
                                   'rgba(245, 158, 11, 0.3)'}`,
              marginBottom: '20px'
            }}>
              <span style={{ 
                color: selectedBookingDetail.status === 'completed' ? '#4A8C18' :
                       selectedBookingDetail.status === 'cancelled' ? '#f87171' :
                       selectedBookingDetail.status === 'confirmed' ? '#60a5fa' :
                       selectedBookingDetail.status === 'in_progress' ? '#4ecdc4' :
                       '#fbbf24',
                fontWeight: '600',
                textTransform: 'uppercase'
              }}>
                Status: {selectedBookingDetail.status?.replace('_', ' ')}
              </span>
            </div>

            {/* Booking Details */}
            <div style={{ display: 'grid', gap: '12px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', gap: '8px', color: isLightMode ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)' }}>
                <Icon name="calendar" color="secondary" darkMode={darkMode} size={18} />
                <span>{selectedBookingDetail.booking_date}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', color: isLightMode ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)' }}>
                <Icon name="schedule" color="secondary" darkMode={darkMode} size={18} />
                <span>{selectedBookingDetail.start_time?.substring(0, 5)} - {selectedBookingDetail.end_time?.substring(0, 5)}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', color: isLightMode ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)' }}>
                <Icon name="service" color="secondary" darkMode={darkMode} size={18} />
                <span>{selectedBookingDetail.service_template?.name || 'N/A'}</span>
              </div>
              {selectedBookingDetail.bike && (
                <div style={{ display: 'flex', gap: '8px', color: '#4A8C18' }}>
                  <Icon name="bike" color="brand" darkMode={darkMode} size={18} />
                  <span style={{ fontWeight: '600' }}>
                    {selectedBookingDetail.bike.brand} {selectedBookingDetail.bike.model}
                  </span>
                </div>
              )}
              {selectedBookingDetail.user && (
                <div style={{ display: 'flex', gap: '8px', color: isLightMode ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)' }}>
                  <Icon name="profile" color="secondary" darkMode={darkMode} size={18} />
                  <span>{selectedBookingDetail.user.firstname} {selectedBookingDetail.user.surname}</span>
                </div>
              )}
              {selectedBookingDetail.technician && (
                <div style={{ display: 'flex', gap: '8px', color: isLightMode ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)' }}>
                  <Icon name="technician" color="secondary" darkMode={darkMode} size={18} />
                  <span>{selectedBookingDetail.technician.name}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  const params = new URLSearchParams();
                  params.append('bookingId', selectedBookingDetail.id);
                  if (selectedBookingDetail.booking_date) {
                    params.append('date', selectedBookingDetail.booking_date);
                  }
                  navigate(`/calendar?${params.toString()}`);
                  setShowBookingDetailModal(false);
                  setSelectedBookingDetail(null);
                }}
                className="btn btn-primary"
                style={{ flex: 1 }}
              >
                <Icon name="calendar" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                View in Calendar
              </button>
              {selectedBookingDetail.bike && (
                <button
                  onClick={() => {
                    setShowBookingDetailModal(false);
                    setSelectedBookingDetail(null);
                    setPassportBike(selectedBookingDetail.bike);
                  }}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  <Icon name="bike" color="secondary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} /> View Bike Passport
                </button>
              )}
            </div>

            {/* Quick Status Update - only for non-completed/cancelled */}
            {selectedBookingDetail.status !== 'completed' && selectedBookingDetail.status !== 'cancelled' && (
              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: isLightMode ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)' }}>
                <p style={{ fontSize: '13px', marginBottom: '12px', color: isLightMode ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.5)' }}>Quick status update:</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {['confirmed', 'in_progress', 'completed', 'cancelled'].map(status => (
                    <button
                      key={status}
                      onClick={async () => {
                        try {
                          await bookingAPI.updateBookingStatus(selectedBookingDetail.id, status);
                          fetchData();
                          setShowBookingDetailModal(false);
                          setSelectedBookingDetail(null);
                        } catch (err) {
                          console.error('Failed to update status:', err);
                        }
                      }}
                      disabled={selectedBookingDetail.status === status}
                      style={{
                        padding: '8px 14px',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: selectedBookingDetail.status === status ? 'not-allowed' : 'pointer',
                        background: selectedBookingDetail.status === status 
                          ? (isLightMode ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)')
                          : status === 'completed' ? 'rgba(26, 40, 17, 0.15)' :
                            status === 'cancelled' ? 'rgba(239, 68, 68, 0.15)' :
                            status === 'confirmed' ? 'rgba(59, 130, 246, 0.15)' :
                            'rgba(78, 205, 196, 0.15)',
                        color: selectedBookingDetail.status === status 
                          ? (isLightMode ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.3)')
                          : status === 'completed' ? '#4A8C18' :
                            status === 'cancelled' ? '#dc2626' :
                            status === 'confirmed' ? '#2563eb' :
                            '#0d9488',
                        fontSize: '12px',
                        textTransform: 'capitalize',
                        fontWeight: selectedBookingDetail.status === status ? '700' : '500'
                      }}
                    >
                      {status.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {passportBike && user && (
        <BikePassportModal
          bikeId={passportBike.id}
          bikeLabel={[passportBike.brand, passportBike.model].filter(Boolean).join(' ') || undefined}
          onClose={() => setPassportBike(null)}
          darkMode={darkMode}
          user={user}
          onLogout={onLogout}
          onToggleDarkMode={onToggleDarkMode}
        />
      )}
    </Layout>
  );
}

export default AdminDashboard;
